import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormMessage } from './FormMessage';
import { useFieldA11y } from './fieldA11y';

export interface SelectFieldOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectFieldProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'aria-invalid' | 'onChange' | 'value'
  > {
  label: ReactNode;
  value: string;
  options: SelectFieldOption[];
  onValueChange: (value: string) => void;
  helperText?: string;
  error?: string;
  placeholder?: string;
  containerClassName?: string;
  triggerClassName?: string;
  name?: string;
  required?: boolean;
}

export const SelectField = ({
  id,
  label,
  value,
  options,
  onValueChange,
  helperText,
  error,
  placeholder,
  className,
  containerClassName,
  triggerClassName,
  required,
  disabled,
  name,
  ...buttonProps
}: SelectFieldProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { fieldId, helperId, errorId, describedByIds } = useFieldA11y({
    id,
    describedBy: buttonProps['aria-describedby'],
    helperText,
    error,
  });
  const listboxId = `${fieldId}-listbox`;
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );
  const enabledOptions = useMemo(
    () => options.filter((option) => !option.disabled),
    [options],
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        rootRef.current &&
        event.target instanceof Node &&
        !rootRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isOpen]);

  const selectValue = (nextValue: string) => {
    const nextOption = options.find((option) => option.value === nextValue);

    if (!nextOption || nextOption.disabled) {
      return;
    }

    onValueChange(nextValue);
    setIsOpen(false);
  };

  const moveSelection = (direction: 1 | -1) => {
    if (enabledOptions.length === 0) {
      return;
    }

    const currentIndex = enabledOptions.findIndex((option) => option.value === value);
    const nextIndex =
      currentIndex === -1
        ? direction === 1
          ? 0
          : enabledOptions.length - 1
        : (currentIndex + direction + enabledOptions.length) % enabledOptions.length;

    onValueChange(enabledOptions[nextIndex].value);
    setIsOpen(true);
  };

  return (
    <div
      ref={rootRef}
      className={[
        'form-field',
        'form-select',
        isOpen ? 'form-select--open' : '',
        error ? 'form-field--invalid' : '',
        containerClassName,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="form-field__label" id={`${fieldId}-label`}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      <button
        id={fieldId}
        type="button"
        className={['form-field__input', 'form-select__trigger', triggerClassName]
          .filter(Boolean)
          .join(' ')}
        role="combobox"
        aria-labelledby={`${fieldId}-label ${fieldId}`}
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedByIds}
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveSelection(1);
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveSelection(-1);
          }

          if (event.key === 'Escape') {
            setIsOpen(false);
          }
        }}
        {...buttonProps}
      >
        <span>{selectedOption?.label ?? placeholder ?? ''}</span>
        <span className="form-select__chevron" aria-hidden="true" />
      </button>
      {name && <input type="hidden" name={name} value={value} />}
      {isOpen && (
        <ul className="form-select__listbox" id={listboxId} role="listbox">
          {options.map((option) => {
            const isSelected = option.value === value;

            return (
              <li
                key={option.value}
                className={[
                  'form-select__option',
                  isSelected ? 'form-select__option--selected' : '',
                  option.disabled ? 'form-select__option--disabled' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled}
                onClick={() => selectValue(option.value)}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}
      <FormMessage id={helperId}>{helperText}</FormMessage>
      <FormMessage id={errorId} tone="error">
        {error}
      </FormMessage>
    </div>
  );
};
